<template>
  <b-card>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ title }}</h4>
        <b-form-input
          v-model.trim="productNameQuery"
          size="sm"
          class="w-auto ml-50"
          placeholder="Urun adi ara"
        />
        <b-form-input
          v-model.trim="barcodeQuery"
          size="sm"
          class="w-auto ml-50"
          placeholder="Barkod ara"
        />
        <b-form-input
          v-model.trim="brandQuery"
          size="sm"
          class="w-auto ml-50"
          placeholder="Marka ara"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="period"
          :options="periodOptions"
          size="sm"
          class="w-auto"
          @change="fetchProductSales"
        />
        <b-button
          size="sm"
          variant="outline-primary"
          class="ml-50"
          :disabled="!items.length"
          @click="toggleShowAll"
        >
          {{ showAll ? 'Ilk 10' : 'Tumu' }}
        </b-button>
      </div>
    </div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-table
        v-if="tableItems.length"
        small
        striped
        hover
        responsive
        :items="tableItems"
        :fields="fields"
        :sort-by="sortBy"
        :sort-desc="sortDesc"
        :sort-compare="sortCompare"
        :sort-internal="false"
        @sort-changed="onSortChanged"
      >
        <template #cell(product_image_url)="data">
          <button
            v-if="resolveImageUrl(data.item.product_image_url)"
            type="button"
            class="p-0 border-0 bg-transparent"
            @click="openImagePreview(data.item.product_image_url)"
          >
            <b-img
              :src="resolveImageUrl(data.item.product_image_url)"
              alt="Urun"
              fluid
              rounded
              style="width: 56px; height: 80px; object-fit: cover; object-position: center top;"
            />
          </button>
          <span
            v-else
            class="text-muted"
          >-</span>
        </template>
        <template #cell(sum_total_price)="data">
          <div>{{ formatCurrency(data.item.sum_total_price) }}</div>
          <small v-if="unitPrice(data.item.sum_total_price, data.item.sum_quantity)" class="text-muted d-block">
            ({{ formatNumber(unitPrice(data.item.sum_total_price, data.item.sum_quantity)) }})
          </small>
        </template>
        <template #cell(sum_total_sales_price)="data">
          <div>{{ formatCurrency(data.item.sum_total_sales_price) }}</div>
          <small v-if="unitPrice(data.item.sum_total_sales_price, data.item.sum_quantity)" class="text-muted d-block">
            ({{ formatNumber(unitPrice(data.item.sum_total_sales_price, data.item.sum_quantity)) }})
          </small>
        </template>
        <template #cell(sum_selected_purchase_price)="data">
          <div>{{ formatCurrency(data.item.sum_selected_purchase_price) }}</div>
          <small
            v-if="purchaseUnitPrice(data.item)"
            class="text-muted d-block"
          >
            ({{ formatNumber(purchaseUnitPrice(data.item)) }})
          </small>
        </template>
      </b-table>
      <div v-else class="text-muted p-1">
        Veri bulunamadi.
      </div>
    </b-overlay>
    <b-modal
      v-model="isImagePreviewOpen"
      title="Urun Gorseli"
      size="lg"
      centered
      hide-footer
      body-class="p-2 p-md-3"
    >
      <div class="text-center">
        <b-img
          v-if="previewImageUrl"
          :src="previewImageUrl"
          alt="Urun gorseli buyuk"
          fluid
          rounded
          style="max-height: 75vh; max-width: 100%; width: auto; object-fit: contain;"
        />
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import { API_ROOT_URL } from '@/libs/serviceUrls'
import axios from '@/libs/axios'

export default {
  name: 'HomeProductSales',
  computed: {
    title() {
      const selected = this.periodOptions.find(option => option.value === this.period)
      const label = selected ? selected.text : 'Bugun'
      return `${label} Urun Satislari`
    },
    filteredItems() {
      let result = this.items
      if (this.productNameQuery) {
        const query = this.productNameQuery.toLowerCase()
        result = result.filter(item => String(item.product_name || '').toLowerCase().includes(query))
      }
      if (this.barcodeQuery) {
        const query = this.barcodeQuery.toLowerCase()
        result = result.filter(item => String(item.product_barcode || '').toLowerCase().includes(query))
      }
      if (this.brandQuery) {
        const query = this.brandQuery.toLowerCase()
        result = result.filter(item => String(item.brand_name || '').toLowerCase().includes(query))
      }
      return result
    },
    sortedItems() {
      if (!this.filteredItems.length) return []
      const sorted = [...this.filteredItems]
      if (!this.sortBy) return sorted
      sorted.sort((left, right) => {
        const result = this.sortCompare(left, right, this.sortBy)
        return this.sortDesc ? result * -1 : result
      })
      return sorted
    },
    tableItems() {
      if (this.showAll) return this.sortedItems
      return this.sortedItems.slice(0, 10)
    },
  },
  data: () => ({
    isLoading: false,
    items: [],
    sortBy: 'sum_total_sales_price',
    sortDesc: true,
    showAll: false,
    isImagePreviewOpen: false,
    previewImageUrl: '',
    productNameQuery: '',
    barcodeQuery: '',
    brandQuery: '',
    period: 'today',
    periodOptions: [
      { value: 'today', text: 'Bugun' },
      { value: 'yesterday', text: 'Dun' },
      { value: 'this_week', text: 'Bu Hafta' },
      { value: 'last_week', text: 'Gecen Hafta' },
      { value: 'this_month', text: 'Bu Ay' },
      { value: 'last_month', text: 'Gecen Ay' },
      { value: 'last_2_months', text: 'Son 2 Ay' },
      { value: 'last_6_months', text: 'Son 6 Ay' },
      { value: 'this_year', text: 'Bu Yil' },
      { value: 'last_year', text: 'Gecen Yil' },
      { value: 'last_2_years', text: 'Son 2 Yil' },
    ],
    fields: [
      { key: 'product_id', label: 'Urun ID', sortable: true },
      { key: 'product_image_url', label: 'Gorsel', sortable: false },
      { key: 'product_name', label: 'Urun', sortable: true },
      { key: 'product_barcode', label: 'Barkod', sortable: true },
      { key: 'brand_name', label: 'Marka', sortable: true },
      { key: 'count', label: 'Siparis', sortable: true },
      { key: 'sum_quantity', label: 'Adet', sortable: true },
      { key: 'sum_total_price', label: 'Toplam Tutar', sortable: true },
      { key: 'sum_total_sales_price', label: 'Satis Tutari', sortable: true },
      { key: 'count_selected_purchase_price', label: 'Alim Adedi', sortable: true },
      { key: 'sum_selected_purchase_price', label: 'Alim Tutari', sortable: true },
    ],
  }),
  created() {
    this.fetchProductSales()
  },
  methods: {
    toggleShowAll() {
      this.showAll = !this.showAll
    },
    openImagePreview(path) {
      const imageUrl = this.resolveImageUrl(path)
      if (!imageUrl) return
      this.previewImageUrl = imageUrl
      this.isImagePreviewOpen = true
    },
    fetchProductSales() {
      this.isLoading = true
      axios
        .get('home/stat/product-sales', { params: { period: this.period } })
        .then(res => {
          const payload = this.extractPayload(res)
          this.items = Array.isArray(payload) ? payload : []
        })
        .catch(() => {
          this.items = []
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    extractPayload(response) {
      if (!response) return null
      let payload = response.data !== undefined ? response.data : response
      if (payload && payload.data !== undefined) payload = payload.data
      if (payload && payload.data !== undefined) payload = payload.data
      return payload
    },
    resolveImageUrl(path) {
      if (!path) return ''
      const rawPath = String(path).replace(/\\/g, '/')
      if (/^https?:\/\//i.test(rawPath)) return rawPath

      const normalizedPath = rawPath.replace(/^\/+/, '')
      return `${API_ROOT_URL}${normalizedPath}`
    },
    formatCurrency(value) {
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) return value
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2,
      }).format(numberValue)
    },
    formatNumber(value) {
      const numberValue = Number(value)
      if (Number.isNaN(numberValue)) return value
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numberValue)
    },
    unitPrice(total, quantity) {
      const totalValue = Number(total)
      const quantityValue = Number(quantity)
      if (Number.isNaN(totalValue) || Number.isNaN(quantityValue) || quantityValue === 0) return null
      return totalValue / quantityValue
    },
    purchaseUnitPrice(item) {
      if (!item) return null
      const purchaseCount = Number(item.count_selected_purchase_price)
      const orderCount = Number(item.count)
      if (Number.isNaN(purchaseCount) || Number.isNaN(orderCount)) return null
      if (purchaseCount !== orderCount) return null
      return this.unitPrice(item.sum_selected_purchase_price, item.sum_quantity)
    },
    sortCompare(a, b, key) {
      const numericKeys = [
        'count',
        'sum_quantity',
        'sum_total_price',
        'sum_total_sales_price',
        'count_selected_purchase_price',
        'sum_selected_purchase_price',
      ]
      if (numericKeys.includes(key)) {
        const left = Number(a[key])
        const right = Number(b[key])
        if (Number.isNaN(left) || Number.isNaN(right)) return 0
        return left > right ? 1 : left < right ? -1 : 0
      }
      const left = a[key] === null || a[key] === undefined ? '' : String(a[key])
      const right = b[key] === null || b[key] === undefined ? '' : String(b[key])
      return left.localeCompare(right, 'tr-TR')
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
    },
  },
}
</script>
